import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { CheckCircle, XCircle, FileText, Eye, Clock, AlertCircle } from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function MyQuotes({ customerEmail }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const quotesRes = await axiosInstance.get('/quotes');
      
      // Filter quotes for this customer
      const customerQuotes = quotesRes.data.filter(q => q.customer_email === customerEmail);
      setQuotes(customerQuotes);
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveQuote = async () => {
    try {
      // Customer just approves - Admin will assign handyman later
      await axiosInstance.post(`/quotes/${selectedQuote.id}/customer-approve`);
      
      toast.success('Quote approved! Our team will schedule your service and assign a technician shortly.');
      setShowApprovalDialog(false);
      setSelectedQuote(null);
      fetchData();
    } catch (error) {
      // Fallback to old endpoint if new one doesn't exist
      try {
        await axiosInstance.patch(`/quotes/${selectedQuote.id}`, {
          status: 'approved'
        });
        toast.success('Quote approved! Our team will schedule your service and assign a technician shortly.');
        setShowApprovalDialog(false);
        setSelectedQuote(null);
        fetchData();
      } catch (err) {
        toast.error('Failed to approve quote. Please try again.');
      }
    }
  };

  const handleRejectQuote = async () => {
    try {
      await axiosInstance.post(`/quotes/${selectedQuote.id}/reject`, null, {
        params: { reason: rejectReason || 'Customer declined' }
      });
      
      toast.success('Quote declined');
      setShowRejectDialog(false);
      setSelectedQuote(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to decline quote');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-500',
      sent: 'bg-blue-500',
      approved: 'bg-teal-500',
      accepted: 'bg-green-500',
      rejected: 'bg-red-500',
      expired: 'bg-orange-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      sent: 'Pending Your Approval',
      approved: 'Approved - Awaiting Assignment',
      accepted: 'Approved - Job Created',
      rejected: 'Declined',
      expired: 'Expired'
    };
    return labels[status] || status.toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Separate pending and processed quotes
  const pendingQuotes = quotes.filter(q => q.status === 'sent');
  const processedQuotes = quotes.filter(q => q.status !== 'sent');

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">My Quotes</h2>
        <p className="text-gray-600">Review and approve your service quotes</p>
      </div>

      {quotes.length === 0 ? (
        <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText size={48} className="text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No quotes available</p>
            <p className="text-gray-400 text-sm mt-2">Quotes will appear here once our team sends them</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Pending Quotes */}
          {pendingQuotes.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Awaiting Your Approval ({pendingQuotes.length})
              </h3>
              {pendingQuotes.map((quote) => (
                <Card key={quote.id} className="backdrop-blur-sm bg-white/90 shadow-lg border-0 border-l-4 border-l-blue-500">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          Quote #{quote.id.substring(0, 8)}
                        </CardTitle>
                        <CardDescription className="mt-2">
                          Created: {formatDate(quote.created_at)}
                          <br />
                          Valid until: {formatDate(quote.valid_until)}
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-teal-600">${quote.total_amount}</p>
                        <Badge className="bg-blue-500 text-white mt-1">
                          {getStatusLabel(quote.status)}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Quote Items Summary */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-sm mb-3">Services Included:</h4>
                        <div className="space-y-2">
                          {quote.items?.map((item, idx) => (
                            <div key={idx} className="text-sm text-gray-600 flex justify-between py-1 border-b border-gray-200 last:border-0">
                              <span>• {item.service} ({item.hours}h)</span>
                              <span className="font-medium">${item.amount}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 pt-3 border-t-2 border-gray-200">
                          <div className="flex justify-between text-sm">
                            <span>Subtotal:</span>
                            <span>${quote.subtotal}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Tax:</span>
                            <span>${quote.tax}</span>
                          </div>
                          {quote.discount > 0 && (
                            <div className="flex justify-between text-sm text-green-600">
                              <span>Discount:</span>
                              <span>-${quote.discount}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold text-lg pt-2 mt-2 border-t">
                            <span>Total:</span>
                            <span className="text-teal-600">${quote.total_amount}</span>
                          </div>
                        </div>
                      </div>

                      {quote.notes && (
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <h4 className="font-medium text-sm mb-1">Notes from our team:</h4>
                          <p className="text-sm text-gray-600">{quote.notes}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-3 pt-4 border-t">
                        <Button
                          onClick={() => {
                            setSelectedQuote(quote);
                            setShowApprovalDialog(true);
                          }}
                          className="flex-1 bg-green-600 hover:bg-green-700 h-12"
                        >
                          <CheckCircle size={18} className="mr-2" />
                          Approve Quote
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedQuote(quote);
                            setShowRejectDialog(true);
                          }}
                          className="flex-1 text-red-600 border-red-300 hover:bg-red-50 h-12"
                        >
                          <XCircle size={18} className="mr-2" />
                          Decline
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Processed Quotes */}
          {processedQuotes.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-500" />
                Past Quotes ({processedQuotes.length})
              </h3>
              {processedQuotes.map((quote) => (
                <Card 
                  key={quote.id} 
                  className="backdrop-blur-sm bg-white/80 shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => { setSelectedQuote(quote); setShowDetailDialog(true); }}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-gray-600">
                          Quote #{quote.id.substring(0, 8)}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {formatDate(quote.created_at)}
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-600">${quote.total_amount}</p>
                        <Badge className={`${getStatusColor(quote.status)} text-white mt-1`}>
                          {getStatusLabel(quote.status)}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-gray-500">
                      {quote.items?.length || 0} service(s) • Total: ${quote.total_amount}
                    </div>
                    {quote.status === 'approved' && (
                      <div className="mt-3 p-3 bg-teal-50 rounded-lg flex items-center gap-2">
                        <Clock className="h-4 w-4 text-teal-600" />
                        <span className="text-sm text-teal-700">
                          Our team will contact you shortly to schedule your service.
                        </span>
                      </div>
                    )}
                    {quote.status === 'accepted' && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-700">
                          Job created! Check "My Jobs" for details.
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Approval Confirmation Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Confirm Quote Approval
            </DialogTitle>
            <DialogDescription>
              By approving this quote, you agree to the services and pricing listed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">Quote Total:</span>
                <span className="text-2xl font-bold text-green-600">${selectedQuote?.total_amount}</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {selectedQuote?.items?.length} service(s) included
              </p>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                What happens next?
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Our team will assign a qualified technician</li>
                <li>• You'll be contacted to confirm a convenient date/time</li>
                <li>• You can track the job status in "My Jobs"</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowApprovalDialog(false)} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleApproveQuote} 
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle size={16} className="mr-2" />
                Approve Quote
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Decline Quote
            </DialogTitle>
            <DialogDescription>
              Please let us know why you're declining this quote (optional).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Price too high, Found another provider, No longer needed..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectReason('');
                }} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRejectQuote} 
                variant="destructive"
                className="flex-1"
              >
                <XCircle size={16} className="mr-2" />
                Decline Quote
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Quote Details #{selectedQuote?.id.substring(0, 8)}</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Created</Label>
                  <p>{formatDate(selectedQuote.created_at)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Valid Until</Label>
                  <p>{formatDate(selectedQuote.valid_until)}</p>
                </div>
                {selectedQuote.service_type && (
                  <div>
                    <Label className="text-gray-500">Service Type</Label>
                    <p>{selectedQuote.service_type}</p>
                  </div>
                )}
                {selectedQuote.service_address && (
                  <div>
                    <Label className="text-gray-500">Service Address</Label>
                    <p>{selectedQuote.service_address}</p>
                  </div>
                )}
              </div>
              <div className="p-4 bg-gray-50 rounded">
                <h4 className="font-medium mb-3">Services</h4>
                {selectedQuote.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium">{item.name || item.service}</p>
                      {item.description && <p className="text-sm text-gray-500">{item.description}</p>}
                      <p className="text-sm text-gray-500">
                        {item.quantity} {item.unit || 'unit'} x ${item.unit_price || item.rate || 0}
                      </p>
                    </div>
                    <p className="font-medium">${item.total || item.amount || 0}</p>
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>${selectedQuote.subtotal || selectedQuote.total_amount}</span>
                  </div>
                  {selectedQuote.tax > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Tax</span>
                      <span>${selectedQuote.tax}</span>
                    </div>
                  )}
                  {selectedQuote.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount</span>
                      <span>-${selectedQuote.discount}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 mt-2 border-t">
                    <span>Total</span>
                    <span className="text-teal-600">${selectedQuote.total_amount}</span>
                  </div>
                </div>
              </div>
              {selectedQuote.notes && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-sm mb-1">Notes:</h4>
                  <p className="text-sm text-gray-600">{selectedQuote.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
